import type { ReactElement } from "react";
import type { LamaTopMoversOutput } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_SUPPRESSED_BODY,
  LAMA_SUPPRESSED_TITLE,
} from "../lama-report-presentation.js";
import { MoverCard } from "./MoverCard.js";

/**
 * LAMA Top Movers — most improved and slipping areas between the last two
 * waves, ported data-blind from `LamaReportPage`.
 *
 * Three distinct "nothing to show" states, deliberately worded apart:
 * suppressed (withheld for anonymity), `vsBaseline` (a single-wave report has
 * no comparison base, so both lists are empty BY CONSTRUCTION — the flag is a
 * copy selector, not a missing feature), and a genuinely unchanged wave.
 *
 * THREE OUTCOMES, NOT TWO (PB-191 Task 22). `unchanged` carries the areas
 * measured in both waves whose score did not move. They used to fall through both
 * filters and vanish, so three improvements plus five unchanged rendered as three
 * improvements with no sign the other five had been assessed — an area that held
 * steady read as one nobody measured. `unchanged` is OPTIONAL on the wire, so an
 * older resolver's payload still renders as two columns.
 */
export function LamaTopMoversWidget({
  data,
}: WidgetBodyProps<LamaTopMoversOutput>): ReactElement {
  if (data.suppressed) {
    return (
      <div>
        <p className="text-sm font-medium text-pb-text">
          {LAMA_SUPPRESSED_TITLE}
        </p>
        <p className="mt-1 text-sm text-pb-text-muted">
          {LAMA_SUPPRESSED_BODY}
        </p>
      </div>
    );
  }
  const unchanged = data.unchanged ?? [];
  if (
    data.improved.length === 0 &&
    data.slipped.length === 0 &&
    unchanged.length === 0
  ) {
    return (
      <div>
        <p className="text-sm font-medium text-pb-text">No movers yet</p>
        <p className="mt-1 text-sm text-pb-text-muted">
          {data.vsBaseline
            ? "No prior wave to compare against yet — this is the baseline wave."
            : "No areas changed between the last two waves."}
        </p>
      </div>
    );
  }
  /* The third column appears only when the resolver SENT the key. An older
     payload has no `unchanged`, and rendering an empty "Held steady" column for it
     would assert that nothing held steady when the truth is that nobody looked. */
  const showUnchanged = data.unchanged !== undefined;
  return (
    <div
      className={
        showUnchanged
          ? "grid gap-6 @3xl/content:grid-cols-3"
          : "grid gap-6 @3xl/content:grid-cols-2"
      }
    >
      <MoverCard
        title="Most improved"
        tone="healthy"
        movers={data.improved}
        emptyText="No gains this wave."
      />
      <MoverCard
        title="Slipping"
        tone="critical"
        movers={data.slipped}
        emptyText="Nothing slipped this wave."
      />
      {showUnchanged ? (
        <MoverCard
          title="Held steady"
          tone="neutral"
          movers={unchanged}
          emptyText="Nothing held steady this wave."
        />
      ) : null}
    </div>
  );
}
